import React, { createContext, useContext, useEffect, useState } from "react";
import Refund from "./refund";
import { kpiCalls } from "../KPICalls";
import { customToast } from "../../../reusableComponents/customToaster/customToast";
import { useAuth } from "../../../context/API_Client";
import {
  getCalendarInterval,
  getCalendarIntervalForGraph,
  getCustomDateRange,
  getUTCDayRange,
  isEmpty,
} from "../../utils/utils";
import { refundValueCountChartSchema } from "./refundValueCountChartSchema";
import { PieChartPaymentMethodSplitSchema } from "../checkout/subheaders/paymentMethodSplit/PieChartPaymentMethodSplitSchema";
import { updateAllPspSchemaArray } from "../../../reusableComponents/filterUtils";
import { failedTransactionSchema } from "../overview/failedTransactionsSchema";
import { refundFilterSchemaForFailureReason } from "./refundFilterSchema";

const RefundAnalyticsContext = createContext();

export const RefundAnalyticsProvider = ({
  selectedBusinessUnit,
  selectedDropdownValue,
  selectedFilterCurrency,
  refresh,
  singleStartDate,
  startDate,
  endDate,
}) => {
  const { getAllVisualizationData, merchantSettings, getAllModalData } =
    useAuth();
  const [totalRefunds, setTotalRefunds] = useState({});
  const [refundRate, setRefundRate] = useState({});
  const [approved, setApproved] = useState({});
  const [pending, setPending] = useState({});
  const [refundValueCountChartData, setRefundValueCountChartData] = useState();
  const [refundTransactionData, setRefundTransactionData] = useState();
  const [
    modalDataForFailedRefundTransactions,
    setModalDataForFailedRefundTransactions,
  ] = useState([]);
  const [
    failedRefundTransactionTableData,
    setFailedRefundTransactionTableData,
  ] = useState();
  const [
    failedRefundTransactionChartData,
    setFailedRefundTransactionChartData,
  ] = useState();


  const resetKips = () => {
    setTotalRefunds({})
    setRefundRate({})
    setApproved({})
    setPending({})
    setRefundValueCountChartData()
    setRefundTransactionData()
    setFailedRefundTransactionTableData()
    setFailedRefundTransactionChartData()

  }


  // kpi calls
  useEffect(() => {
    if (
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {

      resetKips()
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "overall_gmv_count_aggs_report",
          range: "today",
          offset: "0",
          title: "Total Attempted Refunds",
          countfieldName: "countLocale",
          fieldName: "gmvLocaleFull",
          attributeFilters: {
            operationName: ["Refund", "Partial_Refund"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total count and value of refunds initiated in a particular period",
        },
        (data) => setTotalRefunds(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "refund_ratio_agg_report",
          range: "today",
          offset: "0",
          title: "Refund Rate",
          countfieldName: "refundCountPercent",
          fieldName: "refundValuePercent",
          attributeFilters: {
            operationStatus: "Approved",
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total percentage of refunds initiated in a particular period",
        },
        (data) => {
          setRefundRate(data)
        },
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "overall_gmv_count_aggs_report",
          range: "today",
          offset: "0",
          title: "Approved",
          countfieldName: "countLocale",
          fieldName: "gmvLocaleFull",
          attributeFilters: {
            operationStatus: "Approved",
            operationName: ["Refund", "Partial_Refund"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total count and value of refunds approved in a particular period",
        },
        (data) => setApproved(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "overall_gmv_count_aggs_report",
          range: "today",
          offset: "0",
          title: "Pending",
          countfieldName: "count",
          fieldName: "gmvLocaleFull",
          attributeFilters: {
            operationStatus: "Posted",
            operationName: ["Refund", "Partial_Refund"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total count and value of refunds pending in a particular period",
        },
        (data) => setPending(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
    }
  }, [
    selectedBusinessUnit,
    selectedDropdownValue,
    selectedFilterCurrency,
    refresh,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }

    if (
      singleStartDate ||
      (startDate && endDate) ||
      (selectedDropdownValue &&
        selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency)
    ) {
      const dateRange =
        selectedDropdownValue === "customDate"
          ? getCustomDateRange(singleStartDate, startDate, endDate)
          : getUTCDayRange(selectedDropdownValue);
      const calendarIntervalForGraph = getCalendarIntervalForGraph(
        selectedDropdownValue
      );

      merchantSettings(
        (data) => {
          getAllVisualizationData(
            "refund_ratio_histogram_report",
            {
              ...dateRange,
              calendarInterval: calendarIntervalForGraph,
              attributeFilters: {
                operationStatus: ["Approved"],
                currencyCode: selectedFilterCurrency,
                legalEntity: selectedBusinessUnit,
              },
            },
            function (chartTransactionOverAll) {
              setRefundValueCountChartData(
                refundValueCountChartSchema(
                  chartTransactionOverAll?.points,
                  selectedDropdownValue,
                  data?.locale,
                  singleStartDate,
                  startDate,
                  endDate
                )
              );
            },
            function (errorMsg) {
              customToast("error", "high", errorMsg);
            }
          );
        },
        (errMsg) => customToast("error", "high", errMsg)
      );

      const calendarInterval = getCalendarInterval(selectedDropdownValue);
      const attributeFilters = {
        operationStatus: ["Approved"],
        operationName: ["Refund", "Partial_Refund"],
        currencyCode: selectedFilterCurrency,
        legalEntity: selectedBusinessUnit,
      };

      getAllVisualizationData(
        "payment_method_gmv_count_aggs_report",
        {
          ...dateRange,
          calendarInterval: calendarInterval,
          attributeFilters: attributeFilters,
        },
        (data) => {
          setRefundTransactionData(
            PieChartPaymentMethodSplitSchema(
              data?.points,
              data?.metrics,
              "count"
            )
          );
        },
        (errMsg) => customToast("error", "high", errMsg)
      );
    }
  }, [
    selectedDropdownValue,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    getAllModalData(
      function (data) {
        updateAllPspSchemaArray(
          refundFilterSchemaForFailureReason,
          "pspCode",
          data?.pspCodes
        );
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  }, []);

  useEffect(() => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }
    if (
      singleStartDate ||
      (startDate && endDate) ||
      (selectedDropdownValue &&
        selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency)
    ) {
      const dateRange =
        selectedDropdownValue === "customDate"
          ? getCustomDateRange(singleStartDate, startDate, endDate)
          : getUTCDayRange(selectedDropdownValue);
      const attributeFilters = {
        operationStatus: ["Failed", "Rejected"],
        operationName: ["Refund", "Partial_Refund"],
        currencyCode: selectedFilterCurrency,
        legalEntity: selectedBusinessUnit,
      };

      modalDataForFailedRefundTransactions.forEach((item) => {
        switch (item?.id) {
          case "pspCode":
            attributeFilters.pspCode = item.value;
            break;
          case "paymentMethodType":
            attributeFilters.paymentMethodType = item.value;
            break;
          default:
            break;
        }
      });

      getAllVisualizationData(
        "status_reason_aggs_report",
        {
          ...dateRange,
          attributeFilters: attributeFilters,
        },
        function (data) {
          setFailedRefundTransactionChartData(
            failedTransactionSchema(data?.points, data?.metrics, null, null)
          );
          setFailedRefundTransactionTableData(
            failedTransactionSchema(null, null, data?.points, data?.metrics)
          );
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
    }
  }, [
    selectedDropdownValue,
    modalDataForFailedRefundTransactions,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  return (
    <RefundAnalyticsContext.Provider
      value={{
        totalRefunds,
        refundRate,
        approved,
        pending,
        refundValueCountChartData,
        refundTransactionData,
        setModalDataForFailedRefundTransactions,
        failedRefundTransactionTableData,
        failedRefundTransactionChartData,
        singleStartDate,
      }}
    >
      <Refund />
    </RefundAnalyticsContext.Provider>
  );
};

export const RefundAnalyticsStates = () => {
  return useContext(RefundAnalyticsContext);
};
